import { useCallback, useMemo, useState } from 'react';

import {
  useGetInventoriesByType,
  useGetMeasurementRangeSubSensor,
  WindowSize,
} from '@restDataAccess';
import {
  InventoryProvider,
  InventoryTree,
  InventoryType,
  MappingCollections,
  MeasurementRangeQuery,
  SortDirection,
} from '@scaleaq/sitecontrol-lib';
import { DateTime, Duration } from 'luxon';

import {
  AggregatedChartConfigType,
  ChartAggregationResolution,
  ChartInterval,
  ChartType,
  PlotType,
} from '@/components/chart/types.ts';
import { ADULT_FEMALE_LICE_REFERENCE_VALUE } from '@/constants';
import { usePeriodicCallback } from '@/hooks';
import { useSiteContext } from '@/providers';
import { toApiCalendarDay, toApiDate } from '@/utils';

import { liceColors } from './colorLookup.ts';
import { getLatestRangeMean } from './liceCountWidget.functions.ts';

const liceCountStaleTimeMs = Duration.fromObject({ hours: 1 }).toMillis();

/** How much history the lice chart shows. Not the same as the moving-average `windowSize`. */
const LICE_CHART_SPAN_DAYS = 7;

type UseLiceCountProps = {
  /** Set to false to hold the request back, e.g. to fetch providers one after the other. */
  enabled?: boolean;
  provider: InventoryProvider;
  inventoryId: string;
  windowSize: WindowSize;
};

/**
 * Reads the latest lice count for one provider.
 *
 * `isLoading` and `isSettled` are deliberately not inverses. `isLoading` is what the row renders;
 * `isSettled` is what releases the next provider in the chain. A query that is disabled, still
 * waiting its turn, or retrying counts as pending, so a row keeps its spinner until its own request
 * lands. The chain cannot wait for that, because a rate-limited query retries forever, so a failed
 * attempt releases the next provider while this row goes on loading.
 *
 * Releasing on the failure count rather than on `isRateLimited` keeps the gate from reopening: a
 * 429 whose next retry fails with an ordinary error clears `isRateLimited` while the query is still
 * pending, which would un-settle this row and pull the rows behind it back into their spinners with
 * values already on screen. See `docs/rate-limiting.md`.
 */
export const useLiceCount = ({
  enabled = true,
  inventoryId,
  provider,
  windowSize,
}: UseLiceCountProps) => {
  const [liceMeasurementRangesQuery, setLiceMeasurementRangesQuery] = useState(() =>
    getLiceMeasurementRangesQuery({ windowSize, provider, inventoryId })
  );

  // Keep the previous query when the recomputed one is equal (the day-snapped window only
  // changes at midnight), so the hourly tick does not re-render subscribers for nothing.
  const updateRangeQuery = useCallback(
    () =>
      setLiceMeasurementRangesQuery(previous => {
        const next = getLiceMeasurementRangesQuery({ windowSize, provider, inventoryId });
        return isSameLiceQuery(previous, next) ? previous : next;
      }),
    [windowSize, provider, inventoryId]
  );

  const updateInterval = useMemo(() => Duration.fromObject({ hours: 1 }), []);

  usePeriodicCallback(updateRangeQuery, updateInterval, 'LiceCountWidgetContent');

  const { data, failureCount, isError, isPending, isRateLimited } = useGetMeasurementRangeSubSensor(
    liceMeasurementRangesQuery,
    {
      enabled,
      staleTime: liceCountStaleTimeMs,
    }
  );

  const value = useMemo(() => getLatestRangeMean(data?.items?.[inventoryId]), [data, inventoryId]);

  return {
    value,
    isError,
    isRateLimited,
    isLoading: !enabled || isPending,
    isSettled: enabled && (!isPending || failureCount > 0),
  };
};

export const useLiceCountChart = ({
  inventoryId,
  windowSize,
}: {
  windowSize: WindowSize;
  inventoryId: string;
}) => {
  const adultFemale =
    MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemale];
  const mobile = MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountMobile];
  const caligus = MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountCaligus];
  const adultFemaleEggString =
    MappingCollections.inventoryProviderClassPair[InventoryProvider.LiceCountAdultFemaleEggString];
  const adultFemaleNoEggString =
    MappingCollections.inventoryProviderClassPair[
      InventoryProvider.LiceCountAdultFemaleNoEggString
    ];

  // Re-anchor the (historic) chart window periodically so it rolls over at midnight,
  // matching the lice count rows. Without this the window is frozen at mount and never refetches.
  // The previous range is kept while the recomputed one is equal, so the hourly tick does not
  // rebuild the chart config for nothing.
  const [timeRange, setTimeRange] = useState(getLiceChartTimeRange);
  const updateTimeRange = useCallback(
    () =>
      setTimeRange(previous => {
        const next = getLiceChartTimeRange();
        return +previous.queryTo === +next.queryTo ? previous : next;
      }),
    []
  );
  const updateInterval = useMemo(() => Duration.fromObject({ hours: 1 }), []);

  usePeriodicCallback(updateTimeRange, updateInterval, 'useLiceCountChart (LiceCountWidget)');

  const chartConfig: AggregatedChartConfigType = useMemo(
    () => ({
      colors: liceColors,
      from: timeRange.from,
      interval: ChartInterval.Week,
      plot: [
        {
          label: '',
          value: adultFemale,
        },
        {
          label: '',
          value: mobile,
        },
        {
          label: '',
          value: caligus,
        },
        {
          label: '',
          value: adultFemaleEggString,
        },
        {
          label: '',
          value: adultFemaleNoEggString,
        },
      ],
      // The egg-string split is shown in the rows; on the chart it starts hidden behind the legend.
      hiddenPlotTypes: [adultFemaleEggString, adultFemaleNoEggString],
      plotType: PlotType.AGGREGATED,
      reference: { label: '', value: ADULT_FEMALE_LICE_REFERENCE_VALUE },
      resolution: ChartAggregationResolution.DAILY,
      sensors: [{ label: '', value: inventoryId }],
      title: '', // title is not needed for non-widget charts
      to: timeRange.queryTo,
      type: ChartType.HISTORICAL,
      windowSize,
    }),
    [
      adultFemale,
      adultFemaleEggString,
      adultFemaleNoEggString,
      caligus,
      mobile,
      windowSize,
      inventoryId,
      timeRange,
    ]
  );

  return { chartConfig };
};

/**
 * Resolves the OrbitOne camera(s) for the configured lice node.
 *
 * The node may be a single OrbitOne device, a pen (with one or more OrbitOne devices), or a site
 * (with one or more OrbitOne devices). This returns the node itself when it is a camera, plus any
 * camera descendants (cameras whose ancestor `path` contains the node id), sorted alphabetically by
 * name.
 */
export const useOrbitOneCamerasForNode = (inventoryId: string) => {
  const { currentSite } = useSiteContext();
  const { data: orbitOneInventories = [], ...rest } = useGetInventoriesByType({
    inventoryType: InventoryType.OrbitOne,
    inventoryId: currentSite?.id,
  });

  const orbitOneCameras = useMemo<InventoryTree[]>(() => {
    if (!inventoryId) return [];

    return orbitOneInventories
      .filter(inventory => inventory.id === inventoryId || inventory.path?.includes(inventoryId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [orbitOneInventories, inventoryId]);

  return { orbitOneCameras, ...rest };
};

// Helper functions

const getDaysFromWindowSize = (windowSize: WindowSize): number => {
  switch (windowSize) {
    case WindowSize.OneDay:
      return 1;
    case WindowSize.ThreeDays:
      return 3;
    case WindowSize.SevenDays:
      return 7;
    default:
      return 3;
  }
};

/**
 * Builds the historic measurement window.
 *
 * The request ends at the start of today (`queryTo`), the exclusive end of yesterday, so
 * yesterday's bucket stays in the response. The chart axis uses that end and pulls back one day
 * when the open day has no reading. Anchoring both bounds to midnight of the API (Oslo) day keeps
 * the range stable within the day, so the hourly recompute yields a new query key exactly once per
 * day.
 *
 * The span is independent of `windowSize`, which is the moving-average width the API applies within
 * that span.
 */
const getLiceChartTimeRange = (): { from: DateTime; queryTo: DateTime } => {
  const queryTo = toApiCalendarDay(DateTime.now()); // exclusive end of yesterday, in API days
  return {
    from: queryTo.minus({ days: LICE_CHART_SPAN_DAYS }),
    queryTo,
  };
};

const getLiceMeasurementRangesQuery = ({
  provider,
  inventoryId,
  windowSize,
}: {
  provider: InventoryProvider;
  inventoryId: string;
  windowSize: WindowSize;
}): MeasurementRangeQuery => {
  const daysFromWindowSize = getDaysFromWindowSize(windowSize);
  // The window is snapped to the start of today (in API days) — the end of yesterday — so the
  // request is stable and cacheable until midnight, covering the last N full days as calendar dates.
  const end = toApiCalendarDay(DateTime.now());
  const start = end.minus({ days: daysFromWindowSize });
  return {
    inventoryId,
    type: MappingCollections.inventoryProviderClassPair[provider],
    start: toApiDate(start),
    end: toApiDate(end),
    resolution: ChartAggregationResolution.DAILY,
    windowSize,
    includeSubSensors: true,
    mergeSeries: true,
    sortDirection: SortDirection.Ascending,
  };
};

/**
 * Compares the fields of two lice range queries that can actually differ: the identity fields
 * (inventory, provider type, window size) change with the hook props, and the window dates change
 * at midnight. The remaining fields are constants.
 */
const isSameLiceQuery = (a: MeasurementRangeQuery, b: MeasurementRangeQuery): boolean =>
  a.inventoryId === b.inventoryId &&
  a.type === b.type &&
  a.windowSize === b.windowSize &&
  a.start === b.start &&
  a.end === b.end;
